/**
 * AddBidQuestionModal — ask a bidder question, or answer one (Supabase).
 *
 * Opens via CustomEvent('open-add-bid-question-modal', { detail }):
 *   ask:    { campaignId }
 *   answer: { campaignId, questionId, mode: 'answer', prefill: { question, answer } }
 *
 * On success busts ctx.bidCampaignDetailCache[campaignId] + ctx.bidCampaignCache.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'
import { createQuestion, answerQuestion } from '../services/bidCampaigns.js'

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none', resize: 'vertical', minHeight: 60,
}

export default function AddBidQuestionModal() {
  const [open,       setOpen]       = useState(false)
  const [mode,       setMode]       = useState('ask')   // 'ask' | 'answer'
  const [campaignId, setCampaignId] = useState(null)
  const [questionId, setQuestionId] = useState(null)
  const [question,   setQuestion]   = useState('')
  const [answer,     setAnswer]     = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setMode(d.mode === 'answer' ? 'answer' : 'ask')
      setCampaignId(d.campaignId || null)
      setQuestionId(d.questionId || null)
      setQuestion(d.prefill?.question || '')
      setAnswer(d.prefill?.answer || '')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-bid-question-modal', onOpen)
    return () => window.removeEventListener('open-add-bid-question-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    try {
      if (mode === 'answer') {
        if (!questionId) { setSubmitting(false); return }
        await answerQuestion(questionId, answer)
      } else {
        if (!question.trim() || !campaignId) { setSubmitting(false); return }
        const memberName = ctx.currentMember?.fields?.['Full Name'] || ctx.currentMember?.fields?.['Name'] || ''
        await createQuestion(campaignId, { question, askedByMemberId: wf.currentMemberId || undefined, askedByName: memberName || undefined })
      }
      if (ctx.bidCampaignDetailCache && campaignId) ctx.bidCampaignDetailCache[campaignId] = undefined
      ctx.bidCampaignCache = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = (mode === 'answer' ? answer.trim() : question.trim()) && !submitting

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddBidQuestionModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {mode === 'answer' ? 'Answer Question' : 'Add Bidder Question'}
          </span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mode === 'answer' ? (
            <>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '8px 10px' }}>
                {question || '(question)'}
              </div>
              <div>
                <label style={labelStyle}>Answer</label>
                <textarea style={inputStyle} value={answer} onChange={e => setAnswer(e.target.value)} placeholder="Type the answer…" />
              </div>
            </>
          ) : (
            <div>
              <label style={labelStyle}>Question</label>
              <textarea style={inputStyle} value={question} onChange={e => setQuestion(e.target.value)} placeholder="Bidder's question…" />
            </div>
          )}

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : (mode === 'answer' ? 'Save Answer' : 'Add Question')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
